// research-init.js — wires up the cross-module research-grade tabs added
// in the 2026-05 release: fluids Taylor-Green + Falkner-Skan, quantum
// H2 Kolos-Wolniewicz + Hartree-Fock. Reuses the plasma-research pattern.

import init, {
  run_fluids_taylor_green, run_fluids_falkner_skan,
  run_quantum_h2_kolos, run_quantum_hartree_fock,
  build_repro_bundle, repro_bundle_to_bibtex,
} from './engines.js';

let ready = false;

async function bootstrap() {
  try { await init(); ready = true; } catch (e) { console.error('research-init wasm', e); return; }

  bindForm('f-h2kw', form => {
    const req = {
      r_min_bohr: parseFloat(form.r_min.value),
      r_max_bohr: parseFloat(form.r_max.value),
      n_r: parseInt(form.n_r.value),
    };
    const t0 = performance.now();
    const res = JSON.parse(run_quantum_h2_kolos(JSON.stringify(req)));
    const dt = performance.now() - t0;
    setStatus('s-h2kw',
      `${dt.toFixed(1)} ms · R_eq = ${res.r_eq_bohr.toFixed(3)} bohr · E_min = ${res.e_min_hartree.toFixed(6)} Eh · K-W deviation = ${res.deviation_meV.toFixed(1)} meV`);
    chart('c-h2kw', res.points.map(p => p.r_angstrom.toFixed(3)), [
      { label: 'E(R) [Eh]', data: res.points.map(p => p.e_hartree), borderColor: '#4fc3f7', fill: false },
      { label: 'K-W ref [Eh]', data: res.points.map(p => p.e_kw_ref), borderColor: '#fbbf24', borderDash: [4,4], fill: false },
    ], 'R [Å]', 'E [Hartree]');
  });

  bindForm('f-hf', form => {
    const req = {
      atom: form.atom.value,
      max_iter: parseInt(form.max_iter.value),
      tol: parseFloat(form.tol.value),
    };
    const t0 = performance.now();
    const res = JSON.parse(run_quantum_hartree_fock(JSON.stringify(req)));
    const dt = performance.now() - t0;
    // The engine exposes the closed-shell RHF/STO-3G energy as a single honest
    // end-of-SCF point (its per-iteration history is internal); iterations[].iter
    // carries the real SCF count. Open-shell atoms return NaN (RHF not applicable).
    const finite = Number.isFinite(res.final_energy);
    const scfN = res.iterations.length ? res.iterations[res.iterations.length - 1].iter : 0;
    const conv_label = res.converged ? '✓ converged' : (finite ? '⚠ not converged' : '— open-shell (RHF n/a)');
    setStatus('s-hf',
      `${dt.toFixed(1)} ms · ${res.atom} (Z=${res.z}) · `
      + (finite ? `E_HF = ${res.final_energy.toFixed(6)} Eh · ` : 'E_HF = n/a · ')
      + `ref = ${res.reference_energy.toFixed(6)} Eh · ${conv_label}${finite ? ` in ${scfN} SCF iter` : ''}`);

    // Chart: a real multi-point trace if the engine ever surfaces one; otherwise an
    // honest bar comparison of the computed RHF energy vs the Clementi-Roetti HF
    // limit, with the STO-3G basis-set-error gap (NOT a degenerate single-point line).
    const hfCanvas = document.getElementById('c-hf');
    if (hfCanvas && window.Chart) {
      if (hfCanvas._chart) hfCanvas._chart.destroy();
      if (res.iterations.length >= 2) {
        hfCanvas._chart = new Chart(hfCanvas.getContext('2d'), {
          type: 'line',
          data: {
            labels: res.iterations.map(it => it.iter.toString()),
            datasets: [
              { label: 'E_HF(iter)', data: res.iterations.map(it => it.energy), borderColor: '#4fc3f7', fill: false, pointRadius: 3 },
              { label: 'HF limit (Clementi-Roetti)', data: res.iterations.map(() => res.reference_energy), borderColor: '#fbbf24', borderDash: [5, 5], fill: false, pointRadius: 0 },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false, animation: false,
            scales: { x: { title: { display: true, text: 'SCF iteration' } }, y: { title: { display: true, text: 'E [Hartree]' } } } },
        });
      } else {
        const gap = finite ? (res.final_energy - res.reference_energy) : NaN;
        hfCanvas._chart = new Chart(hfCanvas.getContext('2d'), {
          type: 'bar',
          data: {
            labels: ['Computed (RHF/STO-3G)', 'HF limit (Clementi-Roetti)'],
            datasets: [{ label: 'E [Hartree]', data: [finite ? res.final_energy : null, res.reference_energy], backgroundColor: ['rgba(79,195,247,0.75)', 'rgba(251,191,36,0.75)'] }],
          },
          options: { responsive: true, maintainAspectRatio: false, animation: false,
            plugins: { legend: { display: false },
              title: { display: true, text: finite ? `Basis-set error ${gap.toFixed(4)} Eh (STO-3G incompleteness above the HF limit)` : 'Open-shell atom: closed-shell RHF not applicable - no computed energy' } },
            scales: { y: { title: { display: true, text: 'E [Hartree]' } } } },
        });
      }
    }
  });
}

function bindForm(id, handler) {
  const form = document.getElementById(id);
  if (!form) return;
  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (!ready) return;
    window.gdbsShowLoading && window.gdbsShowLoading('Computing...', 'Running validation');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    try { handler(e.target); }
    catch (err) { console.error(`[${id}]`, err); setStatus('s-' + id.slice(2), `Error: ${err.message}`); }
    finally { window.gdbsHideLoading && window.gdbsHideLoading(); }
  });
}

function setStatus(id, msg) {
  const el = document.getElementById(id);
  if (el) el.textContent = msg;
}

function chart(canvasId, labels, datasets, xLabel, yLabel) {
  const canvas = document.getElementById(canvasId);
  if (!canvas || !window.Chart) return;
  if (canvas._chart) canvas._chart.destroy();
  canvas._chart = new Chart(canvas.getContext('2d'), {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      scales: {
        x: { title: { display: true, text: xLabel } },
        y: { title: { display: true, text: yLabel } },
      },
    },
  });
}

// Expose repro-bundle generator for any module that wants to export a result
window.gdbsBuildReproBundle = function(module, tab, inputs, outputs, opts = {}) {
  const req = {
    module, tab,
    inputs: typeof inputs === 'string' ? JSON.parse(inputs) : inputs,
    outputs: typeof outputs === 'string' ? JSON.parse(outputs) : outputs,
    elapsed_ms: opts.elapsed_ms || 0,
    backend: opts.backend || 'cpu',
    geonum_drift: opts.geonum_drift != null ? opts.geonum_drift : null,
    citation_keys: opts.citation_keys || [],
  };
  const bundle = JSON.parse(build_repro_bundle(JSON.stringify(req)));
  const bibtex = repro_bundle_to_bibtex(JSON.stringify(bundle));
  return { bundle, bibtex };
};

// Auto-save session state to backend for any module's last run
window.gdbsSaveSession = async function(module, tab, inputs, bundle, label) {
  const tok = localStorage.getItem('gdbs_token');
  if (!tok) return null;
  try {
    const res = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
      body: JSON.stringify({
        module, tab, label,
        inputs: typeof inputs === 'string' ? inputs : JSON.stringify(inputs),
        bundle: bundle ? (typeof bundle === 'string' ? bundle : JSON.stringify(bundle)) : null,
      }),
    });
    return res.ok ? await res.json() : null;
  } catch (e) {
    console.warn('Session save failed', e);
    return null;
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
